import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, InputNumber, message, Card, Row, Col, Input, Typography, Tag, Empty,
} from "antd";
import {
    SafetyCertificateOutlined, FallOutlined, ApiOutlined, SwapOutlined, AppstoreOutlined,
    CarOutlined, ToolOutlined, TagsOutlined, PieChartOutlined, FileTextOutlined,
    FieldNumberOutlined, CalculatorOutlined, RiseOutlined, SearchOutlined, RightOutlined,
    SettingOutlined,
} from "@ant-design/icons";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { rightsService } from "../../services/rightsService";

const { Text, Title } = Typography;
const PRIMARY = "#242166";
const RIGHT_MODIF_PLAFOND_BONUS = "HPA088";

// Cartes regroupées par thème — SANS intermédiaires / courtiers / taux de commissions
// (déplacés dans « Réseau de distribution »).
const GROUPS = [
    {
        key: "garanties",
        label: "Garanties & primes",
        items: [
            { title: "Garanties", description: "Définir la prime de chaque garantie selon les caractéristiques de l'engin et le capital d'indemnisation.", link: "/garanties", button: "Garanties", icon: <SafetyCertificateOutlined />, color: "#16a34a" },
            { title: "Réductions", description: "Taux de réduction et garanties sur lesquelles ils s'appliquent.", link: "/reductions", button: "Réductions", icon: <FallOutlined />, color: "#0891b2" },
            { title: "Accessoires", description: "Frais d'accessoires des engins suivant leur catégorie.", link: "/parametres/accessoires", button: "Accessoires", icon: <ApiOutlined />, color: "#7c3aed" },
            { title: "Conversions d'énergie", description: "Conversions permettant le calcul de la RC quelle que soit la source d'énergie.", link: "/parametres/conversion", button: "Conversions", icon: <SwapOutlined />, color: "#d97706" },
        ],
    },
    {
        key: "engins",
        label: "Engins",
        items: [
            { title: "Catégories", description: "Catégories d'engins prises en charge par le POOL TPVM-VT GIE.", link: "/categories", button: "Catégories", icon: <AppstoreOutlined />, color: PRIMARY },
            { title: "Carrosseries & Genres", description: "Par catégorie : carrosseries et genres proposés dans les formulaires d'engins.", link: "/parametres/engins-parametres", button: "Carrosseries & genres", icon: <CarOutlined />, color: "#0891b2" },
            { title: "Usages", description: "Usages d'engins suivant leur catégorie.", link: "/parametres/usages", button: "Usages", icon: <ToolOutlined />, color: "#7c3aed" },
            { title: "Marques", description: "Marques d'engins suivant leur catégorie.", link: "/parametres/marques", button: "Marques", icon: <TagsOutlined />, color: "#d97706" },
        ],
    },
    {
        key: "contrat",
        label: "Contrat & émission",
        items: [
            { title: "Fractionnement", description: "Fractionnements applicables sur un contrat.", link: "/parametres/fractionnements", button: "Fractionnement", icon: <PieChartOutlined />, color: "#16a34a" },
            { title: "Avenants", description: "Avenants applicables sur un contrat, avec leurs codes.", link: "/parametres/avenants", button: "Avenants", icon: <FileTextOutlined />, color: PRIMARY },
            { title: "Numéro de police", description: "Format et règles du numéro de police.", link: "/autre-parametres/numero-police", button: "Numéro de police", icon: <FieldNumberOutlined />, color: "#0891b2" },
            { title: "Taxes", description: "Taxes suivant les garanties sur lesquelles elles s'appliquent.", link: "/parametres/taxes", button: "Taxes", icon: <CalculatorOutlined />, color: "#dc2626" },
        ],
    },
];

const ParametresTarificationV2 = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const [plafondBonus, setPlafondBonus] = useState(30);
    const [savingPlafond, setSavingPlafond] = useState(false);
    const canEditPlafond = rightsService(RIGHT_MODIF_PLAFOND_BONUS);

    useEffect(() => {
        axiosInstance.get("/api/parametre/plafond-bonus")
            .then((res) => {
                const v = Number(res.data);
                if (!Number.isNaN(v) && v >= 0) setPlafondBonus(v);
            })
            .catch(() => { /* défaut conservé */ });
    }, []);

    const savePlafondBonus = () => {
        const v = Number(plafondBonus);
        if (Number.isNaN(v) || v < 0) {
            message.error("Le plafond du bonus doit être un nombre positif.");
            return;
        }
        setSavingPlafond(true);
        axiosInstance.put(`/api/parametre/plafond-bonus/${v}`)
            .then(() => message.success(`Plafond du bonus enregistré : ${v} %`))
            .catch(() => message.error("Erreur lors de l'enregistrement du plafond du bonus."))
            .finally(() => setSavingPlafond(false));
    };

    const filteredGroups = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return GROUPS;
        return GROUPS
            .map((g) => ({ ...g, items: g.items.filter((i) => `${i.title} ${i.description}`.toLowerCase().includes(q)) }))
            .filter((g) => g.items.length);
    }, [search]);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"TARIFICATION"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 20, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#fff" }}><SettingOutlined style={{ marginRight: 8 }} />Paramètres de tarification</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>Garanties, engins, contrat et émission</Text>
                                </div>
                                <Input
                                    allowClear
                                    prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.7)" }} />}
                                    placeholder="Rechercher un paramètre…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ maxWidth: 320, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8 }}
                                    styles={{ input: { background: "transparent", color: "#fff" } }}
                                />
                            </div>

                            {/* Plafond du bonus — éditeur en avant */}
                            {!search && (
                                <Card bordered={false} style={{ borderRadius: 14, marginBottom: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                                        <span style={{ width: 48, height: 48, borderRadius: 12, background: `${PRIMARY}14`, color: PRIMARY, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}><RiseOutlined /></span>
                                        <div style={{ flex: 1, minWidth: 220 }}>
                                            <div style={{ fontWeight: 700, color: "#111827", fontSize: 16 }}>Plafond du bonus</div>
                                            <Text type="secondary" style={{ fontSize: 13 }}>
                                                Taux maximal (%) du bonus éditable manuellement sur un contrat. Au-delà, la saisie est ramenée à ce plafond.
                                            </Text>
                                            {!canEditPlafond && <div><Text type="secondary" style={{ fontSize: 12 }}>Modification réservée au Pool. Consultation seule.</Text></div>}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <InputNumber min={0} max={100} step={1} value={plafondBonus} onChange={setPlafondBonus} addonAfter="%" style={{ width: 130 }} disabled={!canEditPlafond} />
                                            <Button type="primary" loading={savingPlafond} onClick={savePlafondBonus} disabled={!canEditPlafond} style={{ background: PRIMARY }}>Enregistrer</Button>
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {/* Groupes de cartes */}
                            {filteredGroups.length === 0 ? (
                                <Empty description="Aucun paramètre trouvé" style={{ padding: "50px 0" }} />
                            ) : filteredGroups.map((g) => (
                                <div key={g.key} style={{ marginBottom: 22 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{g.label}</div>
                                    <Row gutter={[16, 16]}>
                                        {g.items.map((item) => (
                                            <Col xs={24} sm={12} lg={8} xxl={6} key={item.title}>
                                                <Card hoverable bordered={false} onClick={() => navigate(item.link)}
                                                    style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", height: "100%" }} bodyStyle={{ padding: 18, display: "flex", flexDirection: "column", height: "100%" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                                                        <span style={{ width: 42, height: 42, borderRadius: 11, background: `${item.color}14`, color: item.color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                                                        <div style={{ fontWeight: 700, color: "#111827", fontSize: 15 }}>{item.title}</div>
                                                    </div>
                                                    <Text type="secondary" style={{ fontSize: 13, flex: 1 }}>{item.description}</Text>
                                                    <div style={{ marginTop: 14 }}>
                                                        <Button type="link" style={{ padding: 0, color: item.color, fontWeight: 600 }}>{item.button} <RightOutlined /></Button>
                                                    </div>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))}
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default ParametresTarificationV2;
